import { HttpStatus } from '@montugroup/apis-styleguide';
import type { WaitlistAvailability } from '@montugroup/pms-api-contracts';
import { apiPatientPortalContract } from '@montugroup/pms-api-contracts';
import { useQueryClient } from '@tanstack/react-query';
import { isErrorResponse } from '@ts-rest/core';
import { useCallback } from 'react';

import { UPCOMING_CONSULTATIONS_QUERY_KEY } from '@/hooks/consultations/consultationQueryKeys';
import { Logger } from '@/utils/logger';
import { getTsPatientPortalClient } from '@montugroup/circuit-shared/api/patient-portal.client';

const logger = new Logger('useJoinConsultationWaitlist');
const client = getTsPatientPortalClient();

export type WaitlistJoinOutcome = 'joined' | 'notEligible' | 'failed';

// a 409 means the consultation is no longer eligible, e.g. it's now same-day or has been cancelled
const isNotEligibleResponse = (error: unknown): boolean =>
  isErrorResponse(error, apiPatientPortalContract.joinConsultationWaitlist) && error.status === HttpStatus.CONFLICT;

/**
 * Joins the waitlist, or changes the availability preference, and returns the outcome so the consultation card and
 * the post-booking dialog handle it the same way.
 */
export function useJoinConsultationWaitlist() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = client.joinConsultationWaitlist.useMutation({
    retry: false,
    // returning the promise makes mutateAsync wait for the refetch, so callers needn't refetch again; the waitlist
    // only shows on upcoming consultations
    onSuccess: () => queryClient.invalidateQueries({ queryKey: UPCOMING_CONSULTATIONS_QUERY_KEY }),
    // the query client's MutationCache already logs every failed mutation as an error
    onError: (error, { params }) => {
      if (!isNotEligibleResponse(error)) {
        return undefined;
      }
      // an expected race, so only a warning; refresh so no card still offers a join the server has turned down
      logger.warn('consultation no longer eligible for the waitlist', { consultationCode: params.consultationCode });
      return queryClient.invalidateQueries({ queryKey: UPCOMING_CONSULTATIONS_QUERY_KEY });
    },
  });

  const join = useCallback(
    async (consultationCode: string, availability: WaitlistAvailability): Promise<WaitlistJoinOutcome> => {
      try {
        await mutateAsync({ params: { consultationCode }, body: { data: { availability } } });
        return 'joined';
      } catch (error) {
        return isNotEligibleResponse(error) ? 'notEligible' : 'failed';
      }
    },
    [mutateAsync],
  );

  return { join, isPending };
}
